import * as React from "react";
import "./styles.scss";
import { ContentPage, PlayerCard, PlayerCardWidget, Post, SmartLink } from "@/services/bedrock";
import { useRef, useEffect, useCallback, useMemo, useState, useContext } from "react";
import { Png } from "@/assets";
import { convertLiquidVariablesToObject, renderLiquidInObject } from "@/utils/liquidHelpers";
import { Button, Dropdown } from "semantic-ui-react";
import EditUrlModal from "./EditUrlModal";
import LinkToSocialMediaModal from "./LinkToSocialMediaModal";
import { PlayerCardSettingsContext } from "../Contexts";
import { getCountMetricsText } from "../utils";
import { SharePlayerCardDialog } from "../../Players/Dashboard/components/QuickActions/components/SharePlayerCardDialog";

interface PlayerCardPreviewProps {
  onViewMetrics?: () => void;
}

const PlayerCardPreview: React.FC<PlayerCardPreviewProps> = ({ onViewMetrics }) => {
  const {
    Preview,
    Toast,
    ContentPages,
    Posts,
    SmartLinks,
    SocialNetworkSettings: { socialNetworkSettings },
    User,
    PlayerCards,
  } = useContext(PlayerCardSettingsContext);

  const [isEditUrlMenuOpen, setIsEditUrlMenuOpen] = useState<boolean>(false);
  const [isLinkToSocialMediaModalOpen, setIsLinkToSocialMediaModalOpen] = useState<boolean>(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState<boolean>(false);

  const playerId = User.playerId || PlayerCards.selectedPlayerCard?.playerId;

  const [isIFrameConnected, setIsIFrameConnected] = useState<number>(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  let iframeOrigin = window.location.origin;
  if (iframeOrigin.includes("localhost")) iframeOrigin = "http://localhost:4321";

  const effectivePlayerCard: PlayerCard | undefined = useMemo(() => {
    if (!Preview.playerCard) return undefined;
    let widgets = Preview.playerCard.widgets
      .filter(widget => widget.isVisible && widget.sortIndex !== null)
      .map(widget => {
        if (widget.owner.type === "SocialMedia") {
          return {
            ...widget,
            owner: {
              ...widget.owner,
              facebookUrl:
                widget.owner.displayFacebook &&
                socialNetworkSettings.FB?.isPermitted &&
                !!socialNetworkSettings.FB.socialNetwork
                  ? "https://www.facebook.com/"
                  : null,
              instagramUrl:
                widget.owner.displayInstagram &&
                socialNetworkSettings.IG?.isPermitted &&
                !!socialNetworkSettings.IG.socialNetwork
                  ? "https://www.instagram.com/"
                  : null,
              linkedInUrl:
                widget.owner.displayLinkedIn &&
                socialNetworkSettings.LI?.isPermitted &&
                !!socialNetworkSettings.LI.socialNetwork
                  ? "https://www.linkedin.com/"
                  : null,
              xUrl:
                widget.owner.displayX &&
                socialNetworkSettings.TW?.isPermitted &&
                !!socialNetworkSettings.TW.socialNetwork
                  ? "https://www.x.com/"
                  : null,
              tikTokUrl:
                widget.owner.displayTikTok &&
                socialNetworkSettings.TT?.isPermitted &&
                !!socialNetworkSettings.TT.socialNetwork
                  ? "https://www.tikTok.com/"
                  : null,
              googleUrl:
                widget.owner.displayGoogle &&
                socialNetworkSettings.GL?.isPermitted &&
                !!socialNetworkSettings.GL.socialNetwork
                  ? "https://www.google.com/"
                  : null,
              zillowUrl:
                widget.owner.displayZillow &&
                socialNetworkSettings.ZL?.isPermitted &&
                !!socialNetworkSettings.ZL.socialNetwork
                  ? "https://www.zillow.com/"
                  : null,
            },
          };
        } else if (widget.owner.type === "ContentPage") {
          let contentPage: ContentPage | undefined | null;
          if (widget.owner.selectionType === "SPECIFIC") {
            contentPage = widget.owner.contentPage;
            const contentPageId = widget.owner.contentPageId;
            if (contentPage?.id !== contentPageId && contentPageId) {
              contentPage = ContentPages.cachedRecords[contentPageId];
            }
          } else if (widget.owner.selectionType === "RANDOM") {
            contentPage = ContentPages.randomRecord;
          } else if (widget.owner.selectionType === "MOST_RECENT") {
            contentPage = ContentPages.mostRecentRecord;
          }
          return {
            ...widget,
            owner: {
              ...widget.owner,
              contentPage,
            },
          };
        } else if (widget.owner.type === "Post") {
          let post: Post | undefined | null;
          if (widget.owner.selectionType === "SPECIFIC") {
            post = widget.owner.post;
            const postId = widget.owner.postId;
            if (post?.id !== postId && postId) {
              post = Posts.cachedRecords[postId];
            }
          } else if (widget.owner.selectionType === "RANDOM") {
            post = Posts.randomRecord;
          } else if (widget.owner.selectionType === "MOST_RECENT") {
            post = Posts.mostRecentRecord;
          }
          return {
            ...widget,
            owner: {
              ...widget.owner,
              post,
            },
          };
        } else if (widget.owner.type === "SmartLink") {
          let smartLink: SmartLink | undefined | null;
          if (widget.owner.selectionType === "SPECIFIC") {
            smartLink = widget.owner.smartLink;
            const smartLinkId = widget.owner.smartLinkId;
            if (smartLink?.id !== smartLinkId && smartLinkId) {
              smartLink = SmartLinks.cachedRecords[smartLinkId];
            }
          } else if (widget.owner.selectionType === "RANDOM") {
            smartLink = SmartLinks.randomRecord;
          } else if (widget.owner.selectionType === "MOST_RECENT") {
            smartLink = SmartLinks.mostRecentRecord;
          }
          return {
            ...widget,
            owner: {
              ...widget.owner,
              smartLinkId: smartLink?.id ?? null,
              url: smartLink?.destinationUrl,
              thumbnailUrl: smartLink?.ogImageUrl,
              title: widget.owner.title || smartLink?.ogTitle || null,
              description: smartLink?.ogDescription,
            },
          };
        } else {
          return widget;
        }
      })
      .sort((widget1, widget2) => (widget1.sortIndex ?? 0) - (widget2.sortIndex ?? 0));
    if (Preview.playerCard.template) {
      Preview.playerCard.template.widgets.forEach(templateWidget => {
        if (!templateWidget.isVisible) return;
        templateWidget.templateFields.forEach(field => {
          if (!field.isLocked) return;
          widgets
            .filter(widget => {
              return (
                (widget.owner.type === templateWidget.owner.type &&
                  widget.sortIndex !== null &&
                  widget.sortIndex === templateWidget.sortIndex) ||
                ["Header", "Footer"].includes(widget.owner.type)
              );
            })
            .forEach(widget => {
              if (Object.keys(widget.owner).includes(field.fieldName)) {
                (widget.owner as any)[field.fieldName] = (templateWidget.owner as any)[field.fieldName];
              }
            });
        });
      });
    }
    const liquidVariablesObject = convertLiquidVariablesToObject(Preview.playerCard.liquidVariables);
    widgets = renderLiquidInObject(widgets, liquidVariablesObject) as PlayerCardWidget[];
    return { ...Preview.playerCard, widgets } as PlayerCard;
  }, [Preview.playerCard, ContentPages.cachedRecords, Posts.cachedRecords]);

  const sendPayloadToIFrame = (type: "PLAYER_CARD" | "FOCUSED_WIDGET" | "SELECTED_WIDGET", payload: any) => {
    if (!isIFrameConnected || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage({ type, payload }, iframeOrigin);
  };

  useEffect(() => {
    if (isIFrameConnected && effectivePlayerCard) {
      sendPayloadToIFrame("PLAYER_CARD", effectivePlayerCard);
      sendPayloadToIFrame("FOCUSED_WIDGET", Preview.focusedWidgetId);
      sendPayloadToIFrame("SELECTED_WIDGET", Preview.selectedWidgetId);
    }
  }, [isIFrameConnected]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== iframeOrigin) return;
      setIsIFrameConnected(prev => prev + 1);
    },
    [effectivePlayerCard, Preview.focusedWidgetId, Preview.selectedWidgetId]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    sendPayloadToIFrame("PLAYER_CARD", effectivePlayerCard);
  }, [effectivePlayerCard]);

  useEffect(() => {
    sendPayloadToIFrame("FOCUSED_WIDGET", Preview.focusedWidgetId);
  }, [Preview.focusedWidgetId]);

  useEffect(() => {
    sendPayloadToIFrame("SELECTED_WIDGET", Preview.selectedWidgetId);
  }, [Preview.selectedWidgetId]);

  const playerCardUrl = `${effectivePlayerCard?.baseUrl}${effectivePlayerCard?.slug}`;
  const copyPlayerCardUrl = () => {
    navigator.clipboard.writeText(playerCardUrl);
    Toast.showSuccess("Copied!");
  };

  const previewPlayerCard = () => {
    window.open(`${playerCardUrl}?preview=true`, "_blank");
  };

  return (
    <div className="playerCardPreview">
      {effectivePlayerCard && effectivePlayerCard.id > 0 && (
        <>
          <EditUrlModal
            open={isEditUrlMenuOpen}
            playerCard={effectivePlayerCard}
            onClose={() => setIsEditUrlMenuOpen(false)}
          />
          <LinkToSocialMediaModal
            open={isLinkToSocialMediaModalOpen}
            playerCard={effectivePlayerCard}
            onClose={() => setIsLinkToSocialMediaModalOpen(false)}
          />
          <div className="playerCardPreviewButtons">
            {onViewMetrics && (
              <Button
                onClick={onViewMetrics}
                icon="chart bar"
                content={getCountMetricsText(effectivePlayerCard.viewMetricsCount, "View")}
              />
            )}
            <Dropdown text="Share" labeled button className="icon shareDropdown" direction="left">
              <Dropdown.Menu>
                <Dropdown.Header>{playerCardUrl.replace("https://", "").replace("http://", "")}</Dropdown.Header>
                {effectivePlayerCard.isPubliclyAvailable && (
                  <>
                    <Dropdown.Item
                      icon="share alternate"
                      content="Share Player Card"
                      onClick={() => setIsShareDialogOpen(true)}
                    />
                    {User.isPlayer && (
                      <Dropdown.Item
                        icon="linkify"
                        content="Link to Social Media"
                        onClick={() => setIsLinkToSocialMediaModalOpen(true)}
                      />
                    )}
                    <Dropdown.Item icon="external" content="Preview in New Tab" onClick={previewPlayerCard} />
                    <Dropdown.Item icon="copy outline" content="Copy URL" onClick={copyPlayerCardUrl} />
                  </>
                )}
                {!effectivePlayerCard.isPubliclyAvailable && (
                  <div className="note">
                    This Player Card cannot be shared because it is not public. Ensure the player has Player Card access
                    enabled.
                  </div>
                )}
                {User.hasWritePermission && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item icon="pencil" content="Edit URL" onClick={() => setIsEditUrlMenuOpen(true)} />
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </>
      )}
      {effectivePlayerCard && (
        <div className="iframeContainer">
          <img
            src={Png.iPhone15ProMax}
            alt="Preview"
            className={`phoneFrame ${effectivePlayerCard ? "" : "invisible"}`}
          />
          <iframe
            ref={iframeRef}
            key={`${iframeOrigin}/c/preview`}
            className={effectivePlayerCard ? "" : "invisible"}
            src={`${iframeOrigin}/c/preview`}
            seamless
          />
        </div>
      )}
      {playerId && (
        <SharePlayerCardDialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen} playerId={playerId} />
      )}
    </div>
  );
};
export default PlayerCardPreview;
